import { useState } from 'react';
import { generateSafeAndLegalQuiz, submitSafeAndLegalQuiz, GenerateSafeAndLegalQuizOutputType, SubmitSafeAndLegalQuizOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ClipboardCheck, CheckCircle2, XCircle, RotateCcw, Zap, Trophy, Star, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type Question = GenerateSafeAndLegalQuizOutputType['questions'][0];
type Option = Question['options'][0];
type Result = SubmitSafeAndLegalQuizOutputType['results'][0];
type State = 'idle' | 'generating' | 'answering' | 'submitting' | 'results';

interface Props {
  sectionId: string;
  sectionTitle: string;
  sectionBody: string;
}

const LEVEL_CONFIG: Record<string, { label: string; pts: string; color: string; bg: string }> = {
  best:    { label: 'Best answer',    pts: '20 pts', color: 'text-green-700',  bg: 'bg-green-100 border-green-300' },
  good:    { label: 'Good answer',    pts: '10 pts', color: 'text-blue-700',   bg: 'bg-blue-100 border-blue-300'  },
  partial: { label: 'Partial answer', pts: '5 pts',  color: 'text-yellow-700', bg: 'bg-yellow-100 border-yellow-300' },
  wrong:   { label: 'Incorrect',      pts: '0 pts',  color: 'text-red-700',    bg: 'bg-red-100 border-red-300'    },
};

export default function FreeTextQuiz({ sectionId, sectionTitle, sectionBody }: Props) {
  const [state, setState] = useState<State>('idle');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [graded, setGraded] = useState<SubmitSafeAndLegalQuizOutputType | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);

  const start = async () => {
    setState('generating');
    try {
      const res = await generateSafeAndLegalQuiz({
        sectionTitle,
        sectionBody,
        variationSeed: attemptCount > 0 ? `attempt-${attemptCount}` : undefined,
      });
      setQuestions(res.questions);
      setAnswers({});
      setGraded(null);
      setState('answering');
    } catch (e: any) {
      toast.error(e.message || 'Failed to generate quiz');
      setState('idle');
    }
  };

  const submit = async () => {
    const unanswered = questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please select an answer for all ${unanswered.length} remaining question${unanswered.length > 1 ? 's' : ''}`);
      return;
    }
    setState('submitting');
    try {
      const formatted = questions.map(q => ({ questionId: q.id, selectedOptionId: answers[q.id] }));
      const res = await submitSafeAndLegalQuiz({ sectionId, sectionTitle, questions, answers: formatted });
      setGraded(res);
      setAttemptCount(c => c + 1);
      setState('results');
    } catch (e: any) {
      toast.error(e.message || 'Failed to submit');
      setState('answering');
    }
  };

  const retake = () => { setState('idle'); setQuestions([]); setAnswers({}); setGraded(null); };

  const answeredCount = Object.keys(answers).length;

  // ---- IDLE ----
  if (state === 'idle') {
    return (
      <div className="mt-6 border border-primary/20 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <ClipboardCheck className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Safe &amp; Legal Assessment — Choose the Most Correct Answer</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Each question has 4 paragraph-length answers. Three are correct to varying degrees — one is wrong. 
              Your job is to pick the <strong>most correct</strong> answer for maximum points.
            </p>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <span className="flex items-center gap-1 text-green-700 bg-green-100 border border-green-200 rounded px-2 py-0.5">
                <Trophy className="w-3 h-3" /> Best answer = 20 pts
              </span>
              <span className="flex items-center gap-1 text-blue-700 bg-blue-100 border border-blue-200 rounded px-2 py-0.5">
                <Star className="w-3 h-3" /> Good = 10 pts
              </span>
              <span className="flex items-center gap-1 text-yellow-700 bg-yellow-100 border border-yellow-200 rounded px-2 py-0.5">
                <Zap className="w-3 h-3" /> Partial = 5 pts
              </span>
              <span className="flex items-center gap-1 text-red-700 bg-red-100 border border-red-200 rounded px-2 py-0.5">
                <XCircle className="w-3 h-3" /> Wrong = 0 pts
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Max score: <strong>125 pts</strong> (100 for all best answers + 25 bonus)
            </p>
            <Button className="mt-4 gap-2" onClick={start}>
              <ClipboardCheck className="w-4 h-4" />
              {attemptCount > 0 ? 'Retake Assessment' : 'Start Assessment'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- GENERATING ----
  if (state === 'generating') {
    return (
      <div className="mt-6 border border-border rounded-xl p-10 flex flex-col items-center gap-3 bg-card">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <ClipboardCheck className="w-6 h-6 text-primary animate-pulse" />
        </div>
        <p className="font-medium text-foreground">Generating assessment questions…</p>
        <p className="text-sm text-muted-foreground">AI is creating realistic scenario-based questions for this section</p>
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  // ---- RESULTS ----
  if (state === 'results' && graded) {
    const pct = graded.percentage;
    return (
      <div className="mt-6 space-y-4">
        {/* Score banner */}
        <div className={cn(
          'rounded-xl p-5 border',
          graded.passed ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'
        )}>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="text-lg font-bold text-foreground">
                {graded.optimalCount === questions.length ? '🏆 Perfect Score!' : graded.passed ? '✅ Passed!' : '❌ Re-take Recommended'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{graded.overallFeedback}</p>
            </div>
            <div className="text-right shrink-0">
              <p className={cn('text-3xl font-bold',
                pct === 100 ? 'text-green-600' : pct >= 70 ? 'text-primary' : pct >= 50 ? 'text-yellow-600' : 'text-red-500'
              )}>
                {graded.optimalCount}/{questions.length}
              </p>
              <p className="text-xs text-muted-foreground">best answers</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-primary/5 border border-primary/15 rounded-lg px-3 py-2">
            <Zap className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-semibold text-foreground">
              +{graded.totalPoints} points earned
            </span>
            <span className="text-xs text-muted-foreground ml-1">
              (out of {graded.maxPoints} possible)
            </span>
          </div>
        </div>

        {/* Per-question breakdown */}
        {questions.map((q, i) => {
          const r = graded.results.find(x => x.questionId === q.id);
          const selectedOpt = q.options.find(o => o.id === r?.selectedOptionId);
          const bestOpt = q.options.find(o => o.id === q.bestOptionId);
          const cfg = LEVEL_CONFIG[r?.selectedLevel ?? 'wrong'];
          const isOptimal = r?.choseOptimal;

          return (
            <div key={q.id} className={cn(
              'border rounded-xl overflow-hidden',
              isOptimal ? 'border-green-200' : 'border-border'
            )}>
              {/* Question header */}
              <div className="px-4 pt-4 pb-3 flex items-start gap-2">
                {isOptimal
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  : r?.selectedLevel === 'wrong'
                    ? <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    : <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    <span className="text-muted-foreground mr-1.5">Q{i + 1}.</span>{q.question}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={cn('text-xs font-semibold px-2 py-0.5 rounded border', cfg.bg, cfg.color)}>
                      {cfg.label} — {r?.pointsEarned ?? 0} pts earned
                    </span>
                  </div>
                </div>
              </div>

              {/* Options revealed */}
              <div className="px-4 pb-4 space-y-2">
                {q.options.map(opt => {
                  const isSelected = opt.id === r?.selectedOptionId;
                  const isBest = opt.id === q.bestOptionId;
                  const optCfg = LEVEL_CONFIG[opt.level];
                  return (
                    <div
                      key={opt.id}
                      className={cn(
                        'rounded-lg border p-3 text-sm transition-all',
                        isSelected && isBest ? 'border-green-300 bg-green-50/50' :
                        isSelected && !isBest ? 'border-amber-300 bg-amber-50/50' :
                        isBest && !isSelected ? 'border-green-200 bg-green-50/30' :
                        'border-border bg-muted/20 opacity-60'
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <div className="shrink-0 mt-0.5">
                          {isSelected && isBest ? '✅' :
                           isSelected && !isBest ? '⚠️' :
                           isBest ? '✓' : ''}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground leading-snug">{opt.text}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Badge variant="outline" className={cn('text-xs border', optCfg.bg, optCfg.color)}>
                              {optCfg.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{opt.points} pts</span>
                            {isSelected && <span className="text-xs font-semibold text-primary">← your answer</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Explanation */}
                {r?.explanation && (
                  <div className="mt-2 bg-muted/30 border border-border rounded-lg px-3 py-2.5 text-xs text-muted-foreground leading-relaxed">
                    <span className="font-semibold text-foreground">💡 Why?</span>{' '}{r.explanation}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <Button variant="outline" className="w-full gap-2" onClick={retake}>
          <RotateCcw className="w-4 h-4" /> Retake Assessment (New Questions)
        </Button>
      </div>
    );
  }

  // ---- ANSWERING / SUBMITTING ----
  return (
    <div className="mt-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-primary" /> {sectionTitle} Assessment
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{answeredCount}/{questions.length} answered</span>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={retake}>
            Cancel
          </Button>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/15 rounded-lg px-4 py-2.5 text-sm text-foreground flex items-center gap-2">
        <Trophy className="w-4 h-4 text-primary shrink-0" />
        <span>Choose the <strong>most correct</strong> answer for each question — you can still earn partial points for a good answer.</span>
      </div>

      {/* Questions */}
      {questions.map((q, idx) => (
        <div key={q.id} className="border border-border rounded-xl bg-card overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-border/50">
            <p className="text-sm font-semibold text-foreground">
              <span className="text-muted-foreground mr-1.5">Q{idx + 1}.</span>{q.question}
            </p>
          </div>
          <div className="p-3 space-y-2">
            {q.options.map((opt, i) => {
              const isSelected = answers[q.id] === opt.id;
              const letter = ['A', 'B', 'C', 'D'][i];
              return (
                <button
                  key={opt.id}
                  onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.id }))}
                  disabled={state === 'submitting'}
                  className={cn(
                    'w-full text-left rounded-lg border p-3 transition-all text-sm',
                    isSelected
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                      : 'border-border hover:border-primary/40 hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className={cn(
                      'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 border',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground'
                    )}>
                      {letter}
                    </span>
                    <p className={cn('leading-snug flex-1', isSelected ? 'text-foreground' : 'text-muted-foreground')}>
                      {opt.text}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <Button
        className="w-full text-base py-3 h-auto gap-2"
        onClick={submit}
        disabled={state === 'submitting' || answeredCount < questions.length}
      >
        {state === 'submitting'
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
          : answeredCount < questions.length
            ? `Select an answer for all questions (${questions.length - answeredCount} remaining)`
            : '🎯 Submit Assessment'}
      </Button>
    </div>
  );
}
